import { Info, Plus, Trash2 } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { MissionProductFieldset } from "@/components/missions/mission-product-fieldset"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import type {
  CatalogPickerPayload,
  MissionProductFormData,
} from "@/types/missions"

/**
 * OKS-990 — the offer as a repeatable list (UX Spec §S7).
 *
 * A drop may offer several products; the creator picks exactly one. Each entry
 * is the shipped `MissionProductFieldset` in full — catalog attach / manual
 * entry, name, quantity, value, currency, SKU, stock, gift TTL, variant
 * pinning — preceded by a position header and followed by a remove control.
 *
 * # ⚠️ ROWS ARE ADDRESSED BY INDEX, and the index is the posted name
 *
 * Every field posts `mission[mission_products_attributes][<i>][...]` and every
 * nested error arrives as `mission_products[<i>].<attr>` (`index_errors: true`
 * on the association). Those two indices are the SAME index, and that is the
 * only reason a multi-row form can say which product is wrong — without it
 * Rails emits `mission_products.currency` and the brand is told "currency is
 * invalid" with no way to know which row.
 *
 * # ⚠️ Removing a PERSISTED row posts `_destroy`, it does not just unmount
 *
 * Dropping the fields from the DOM tells the server nothing — the row would
 * survive the save untouched. A persisted row stays mounted with a hidden
 * `_destroy` + `id` so Rails destroys it; an unsaved row is simply forgotten,
 * because there is nothing to destroy.
 *
 * ⚠️ The server refuses removing a product a creator has already been granted
 * (PRD A7), as a field error rather than the FK's `InvalidForeignKey`. This
 * control is the courtesy; `MissionsController#refuse_removal_of_granted_products`
 * is the gate.
 */
export function MissionProductsSection({
  products,
  errors,
  onDirty,
  catalog,
  canDetach,
}: {
  products: MissionProductFormData[]
  errors: Record<string, unknown>
  onDirty: () => void
  /**
   * ⚠️ REQUIRED, not optional, and `| undefined` is the value not the absence.
   * `MissionProductFieldset` documents why: as `catalog?:` a parent that never
   * passed it TYPE-CHECKED, the picker took its unresolved branch forever and
   * was permanently empty — server correct, component correct, wiring absent,
   * and nothing able to see it. Keeping it required here makes the compiler
   * enumerate this new call site too, which is exactly what it just did.
   */
  catalog: CatalogPickerPayload | undefined
  canDetach: boolean
}) {
  const { t } = useTranslation()

  // Rows carry a stable key so React never re-keys on index — removing row 0
  // would otherwise shift every subsequent row's identity and hand row 1's
  // uncontrolled input values to row 0.
  const [rows, setRows] = useState(() =>
    products.length > 0
      ? products.map((product, i) => ({ key: `p-${product.id ?? i}`, product }))
      : [{ key: "p-new-0", product: null as MissionProductFormData | null }],
  )
  const [removed, setRemoved] = useState<MissionProductFormData[]>([])

  function addRow() {
    setRows((current) => [
      ...current,
      { key: `p-new-${current.length}-${Date.now()}`, product: null },
    ])
    onDirty()
  }

  function removeRow(key: string) {
    setRows((current) => {
      const row = current.find((entry) => entry.key === key)
      // A persisted row must travel to the server as `_destroy`; an unsaved one
      // has nothing to destroy and is simply dropped.
      if (row?.product?.id != null) {
        setRemoved((gone) => [...gone, row.product!])
      }
      return current.filter((entry) => entry.key !== key)
    })
    onDirty()
  }

  return (
    <section
      className="flex flex-col gap-4 rounded-md border border-dashed p-4"
      data-testid="mission-products-section"
    >
      <div className="space-y-1">
        <Label className="text-base">
          {t("missions.form.products.legend")}
        </Label>
        <p className="text-muted-foreground text-sm">
          {t("missions.form.products.helper")}
        </p>
      </div>

      {/* OKS-1039 (AC4) — a drop/promotional with no recorded product is
          ALLOWED: the creator is still asked to confirm a shipping address
          (the brand queue waits on it). The hint says so and nudges toward a
          structured product; it is not an error.

          ⚠️ Keyed on the SAVED products (`products`, the server prop), NOT on
          `rows`. `rows` is never empty — a product-less mission opens with one
          blank row and the last row has no remove control — so a row-count
          gate would never render. A blank row records nothing until it is
          filled and saved, so the hint stays until the mission is saved with
          a product. */}
      {products.length === 0 && (
        <p
          className="text-muted-foreground flex items-start gap-2 text-sm"
          data-testid="mission-products-none-hint"
        >
          <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
          <span>{t("missions.form.products.none_hint")}</span>
        </p>
      )}

      {rows.map((row, index) => (
        <div
          key={row.key}
          className="flex flex-col gap-2"
          data-testid="mission-product-row"
          data-row-index={index}
        >
          {rows.length > 1 && (
            <p className="text-muted-foreground text-xs font-medium">
              {t("missions.form.products.position", { position: index + 1 })}
            </p>
          )}

          <MissionProductFieldset
            index={index}
            product={row.product}
            errors={errors}
            onDirty={onDirty}
            catalog={catalog}
            canDetach={canDetach}
          />

          {rows.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              // ⚠️ A full-row button at <640px rather than an icon crammed into
              // the header (UX §5) — the target has to survive a thumb.
              className="w-full justify-center sm:w-auto"
              onClick={() => removeRow(row.key)}
              data-testid="mission-product-remove"
              aria-label={t("missions.form.products.remove_aria", {
                position: index + 1,
              })}
            >
              <Trash2 aria-hidden="true" className="size-4" />
              {t("missions.form.products.remove")}
            </Button>
          )}
        </div>
      ))}

      {/* ⚠️ Removed PERSISTED rows keep posting, after the live ones, so their
          indices cannot collide with a row still on screen. Unmounting alone
          would leave them saved. */}
      {removed.map((product, offset) => {
        const index = rows.length + offset
        return (
          <div key={`destroy-${product.id}`} hidden>
            <input
              type="hidden"
              name={`mission[mission_products_attributes][${index}][id]`}
              value={String(product.id)}
            />
            <input
              type="hidden"
              name={`mission[mission_products_attributes][${index}][_destroy]`}
              value="1"
            />
          </div>
        )
      })}

      <Button
        type="button"
        variant="outline"
        onClick={addRow}
        data-testid="mission-products-add"
        className="w-full justify-center sm:w-auto"
      >
        <Plus aria-hidden="true" className="size-4" />
        {t("missions.form.products.add")}
      </Button>
    </section>
  )
}
